<script setup>
import { computed } from 'vue'
import ListItem from './ListItem.vue'
import Loading from '@/components/Loading.vue'


// Props
const props = defineProps({
  bets: {
    type: Array,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  statusFilter: {
    type: String,
    default: null
  },
  searchQuery: {
    type: String,
    default: ''
  }
})

// Computed
const filteredBets = computed(() => {
  let filtered = props.bets

  if (props.statusFilter) {
    filtered = filtered.filter(bet => bet.status === props.statusFilter)
  }

  if (props.searchQuery) {
    const query = props.searchQuery.toLowerCase()
    filtered = filtered.filter(bet => 
      bet.event.home_team.toLowerCase().includes(query) ||
      bet.event.away_team.toLowerCase().includes(query) ||
      bet.bet_type.toLowerCase().includes(query)
    )
  }

  // Per non mutare props, creiamo nuovi oggetti per ogni bet e aggiungiamo display_time
  const mapped = filtered.map(bet => {
    const display_time = getCommenceTime(bet)
    // Copia shallow delle proprietà dell'oggetto bet e aggiungi display_time
    return Object.assign({}, bet, { display_time })
  })

  // Ordina per display_time crescente.
  return mapped.sort((a, b) => a.display_time - b.display_time)
})

const getCommenceTime = (bet) => {
  if (bet.event) {
    return new Date(bet.event.commence_time)

  } else if (bet.items && bet.items.length > 0) {
    if (bet.items.length > 0) {
        const lastPendingItem = bet.items.sort((x, y) => new Date(y.event.commence_time) - new Date(x.event.commence_time))[0]
        return new Date(lastPendingItem.event.commence_time)
    }
  }

  return new Date(0) // Fallback se non c'è commence_time
}

const pendingBets = computed(() => {
  return filteredBets.value.filter(bet => bet.status === 'pending')
})

const terminatedBets = computed(() => {
  return filteredBets.value
    .filter(bet => bet.status !== 'pending')
    .sort((a, b) => b.display_time - a.display_time)
})
</script>


<template>
  <!-- Loading -->
  <Loading v-if="loading" :message="$t('myBets.loading')" />

  <!-- Lista scommesse -->
  <div v-else-if="filteredBets.length > 0">
    <!-- Scommesse Pending -->
    <div v-if="pendingBets.length > 0">
      <h3 class="text-h6 mb-4 text-primary font-weight-bold section-header">
        <v-icon class="me-2">mdi-clock-outline</v-icon>
        {{ $t('myBets.sections.pending') }}
      </h3>
      <v-row>
        <v-col v-for="bet in pendingBets" :key="bet.id" cols="12" lg="6">
          <ListItem :bet="bet" />
        </v-col>
      </v-row>
    </div>

    <!-- Divisore se ci sono sia pending che terminate -->
    <v-divider 
      v-if="pendingBets.length > 0 && terminatedBets.length > 0" 
      class="my-6"
    ></v-divider>

    <!-- Scommesse Terminate -->
    <div v-if="terminatedBets.length > 0">
      <h3 class="text-h6 mb-4 text-medium-emphasis font-weight-bold section-header completed">
        <v-icon class="me-2">mdi-flag-checkered</v-icon>
        {{ $t('myBets.sections.completed') }}
      </h3>
      <v-row>
        <v-col v-for="bet in terminatedBets" :key="bet.id" cols="12" lg="6">
          <ListItem :bet="bet" />
        </v-col>
      </v-row>
    </div>
  </div>  <!-- Nessuna scommessa -->
  <div v-else class="text-center py-8">
    <v-icon size="64" color="grey" class="mb-4">mdi-cash-off</v-icon>
    <h3 class="text-h5 mb-2">{{ $t('myBets.empty.title') }}</h3>
    <p class="text-medium-emphasis">
      {{ searchQuery || statusFilter ? $t('myBets.empty.subtitle') : $t('myBets.empty.noData') }}
    </p>
    <v-btn 
      v-if="!searchQuery && !statusFilter" 
      color="primary" 
      class="mt-4"
      @click="$router.push('/list')"
    >
      {{ $t('myBets.empty.startBetting') }}
    </v-btn>
  </div>
</template>


<style scoped>
/* Stili per le sezioni */
.section-header {
  border-left: 4px solid rgb(var(--v-theme-primary));
  padding-left: 16px;
  margin-bottom: 24px;
}

.section-header.completed {
  border-left-color: rgba(var(--v-theme-on-surface), 0.38);
}

/* Divider personalizzato */
.v-divider {
  border-color: rgba(var(--v-theme-primary), 0.12) !important;
  border-width: 2px !important;
}
</style>